// src/components/ContinueCard.jsx
//
// "Continue where you left off" (library-2-shelf). Sits at the top of Your Library and
// only when there is something to continue: a cover thumb in the pack's colour, the
// pack and section titles, that pack's progress bar, and a Resume button that
// deep-links straight back to the section.
//
// The record comes from localStorage (see storage/lastVisited.js) -- no Firestore read
// and no schema. Resume reuses the readers' EXISTING deep-link contract, `?item=<id>`,
// so nothing in the reader had to change to support it.
import React from 'react';
import { getPackTheme } from '../utils/packBadge.js';
import { getPackType } from '../utils/packHelpers.js';
import { IconArrowRight } from './icons.jsx';

// The reader route for a pack, mirroring DashboardPage's openPack().
export function readerPathFor(pack) {
    const type = getPackType(pack);
    if (type === 'set-text') return '/set-text/' + pack.id;
    if (type === 'skills-workshop') return '/skills-workshop/' + pack.id;
    if (type === 'literature-study') return '/literature-study/' + pack.id;
    if (type === 'poetry-anthology') return '/poetry-anthology/' + pack.id;
    return null;   // series / placeholder types have no reader -> no Resume
}

// The full resume target, or null when this pack has no reader.
export function resumeHref(pack, itemId) {
    const base = readerPathFor(pack);
    if (!base) return null;
    return itemId ? base + '?item=' + encodeURIComponent(itemId) : base;
}

export default function ContinueCard({ record, pack, percent, onResume }) {
    if (!record || !pack) return null;
    const href = resumeHref(pack, record.itemId);
    if (!href) return null;

    // library-2c: cover AND ink both come from the derived theme, which already
    // resolves a custom coverColor ahead of the swatch key.
    const theme = getPackTheme(pack);
    const cover = theme.cover;
    const ink = theme.primary;
    const pct = Math.max(0, Math.min(100, Number(percent) || 0));
    const packTitle = record.packTitle || pack.label || '';
    const sectionTitle = record.title || '';

    return (
        <div className="mb-8 rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 md:p-5">
            {/* Wraps on the narrowest phones so the Resume button drops to its own row
                rather than squeezing the titles to a few characters. */}
            <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
                {/* Cover thumb in the pack's own colour */}
                <div className="flex-shrink-0 w-[52px] h-[68px] md:w-[60px] md:h-[80px] rounded-[10px] flex items-end p-2"
                    style={{ background: cover, color: ink }} aria-hidden="true">
                    <span className="font-display font-bold text-[10px] leading-tight line-clamp-3">
                        {pack.spineLabel || pack.label}
                    </span>
                </div>

                <div className="min-w-0 flex-grow">
                    <div className="font-display text-xs font-semibold text-book-muted mb-1">Continue where you left off</div>
                    <h3 className="font-display font-bold text-book-text text-base md:text-lg leading-tight truncate">
                        {sectionTitle || packTitle}
                    </h3>
                    {sectionTitle && packTitle && (
                        <p className="font-serif text-book-muted text-sm truncate">{packTitle}</p>
                    )}

                    {percent !== null && percent !== undefined && (
                        <div className="mt-2.5 flex items-center gap-2.5">
                            <div className="flex-grow h-1.5 rounded-full bg-book-line overflow-hidden"
                                role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                                aria-label={packTitle + ' progress'}>
                                <div className="h-full rounded-full bg-book-accent" style={{ width: pct + '%' }} />
                            </div>
                            <span className="font-display text-xs font-semibold text-book-muted flex-shrink-0">{pct}%</span>
                        </div>
                    )}
                </div>

                <button type="button" onClick={() => onResume(href)}
                    className="flex-shrink-0 w-full sm:w-auto justify-center px-4 md:px-5 py-2.5 font-display text-sm font-semibold btn-sweep flex items-center gap-2">
                    Resume <IconArrowRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
